import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/dashboard.css'; // Reusing your existing styles
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothPramukhWiseSurvey = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Booth Pramukh Survey",
            subtitle: "Overview of survey progress by Booth Pramukh.",
            
            // Search
            searchPlaceholder: "Search name, mobile or booth...",
            
            // Messages
            loading: "Loading Data...",
            noRecordsFound: "No Pramukhs found matching",
            
            // Labels
            totalSurvey: "Total Survey",
            available: "Available",
            unavailable: "Unavailable",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            none: "None",
            
            // Booth placeholder
            boothPlaceholder: "-"
        },
        
        hi: {
            // Page titles
            title: "बूथ प्रमुख सर्वेक्षण",
            subtitle: "बूथ प्रमुख द्वारा सर्वेक्षण प्रगति का अवलोकन।",
            
            // Search
            searchPlaceholder: "नाम, मोबाइल या बूथ खोजें...",
            
            // Messages
            loading: "डेटा लोड हो रहा है...",
            noRecordsFound: "कोई प्रमुख नहीं मिला",
            
            // Labels
            totalSurvey: "कुल सर्वेक्षण",
            available: "उपलब्ध",
            unavailable: "अनुपलब्ध",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            none: "कोई नहीं",
            
            // Booth placeholder
            boothPlaceholder: "-"
        },
        
        gu: {
            // Page titles
            title: "બૂથ પ્રમુખ સર્વે",
            subtitle: "બૂથ પ્રમુખ દ્વારા સર્વે પ્રગતિની ઝાંખી.",
            
            // Search
            searchPlaceholder: "નામ, મોબાઇલ અથવા બૂથ શોધો...",
            
            // Messages
            loading: "ડેટા લોડ થઈ રહ્યો છે...",
            noRecordsFound: "કોઈ પ્રમુખ મળ્યા નથી",
            
            // Labels
            totalSurvey: "કુલ સર્વે",
            available: "ઉપલબ્ધ",
            unavailable: "અનુપલબ્ધ",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            none: "કોઈ નહીં",
            
            // Booth placeholder
            boothPlaceholder: "-"
        }
    };

    const txt = t[lang];
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (appId) {
            fetchData();
        }
    }, [appId]);

    const fetchData = async () => {
        try {
            // Note: Update user_type dynamically if needed, hardcoded to 'BP' for Booth Pramukh based on context
            const response = await axios.post(`${BASE_URL}/api/dis_user_type_wise_survey_dash`, {
                app_id: appId,
                type: "BP" 
            });

            if (response.data.success) {
                setUsers(response.data.data.result || []);
            }
        } catch (error) {
            console.error("Error fetching booth pramukh wise survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Navigation Handler ---
    const handleCardClick = (userId) => {
        // We pass both the user_id and user_type: "BP" in the state
        navigate('/dis_user_wise_survey_voter', { state: { user_id: userId, user_type: "BP" } });
    };

    // Filter Logic
    const filteredUsers = users.filter(user => 
        (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.mobile_no && user.mobile_no.includes(searchTerm)) ||
        (user.booth_no && String(user.booth_no).includes(searchTerm))
    );

    return (
        <div className="app-container dashboard-page">
            <div className='dashboard-content container-fluid p-4'>
                
                {/* --- Header Component Integrated Here --- */}
                <Header 
                    title={txt.title} 
                   
                />

                {/* --- Search Bar Row --- */}
                <div className="row fade-in-up mt-3" style={{ animationDelay: '0.1s' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
                            <div className="search-box-wrapper">
                                <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input 
                                    type="text" 
                                    className="header-search-input" 
                                    placeholder={txt.searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Content List --- */}
                <div className="container-fluid mt-4" style={{ flex: 1 }}>
                    {loading ? (
                        <div className="text-center p-5 text-muted">{txt.loading}</div>
                    ) : (
                        <div className="row g-4">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => {
                                    // Calculate "Available" (Completed) count
                                    const completed = (user.P || 0) + (user.N || 0) + (user.D || 0) + (user.C || 0);

                                    return (
                                        <div key={user.user_id} className="col-12 col-md-6 col-lg-4 fade-in-up">
                                            {/* ADDED: onClick handler and cursor pointer */}
                                            <div 
                                                className="user-survey-card h-100"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleCardClick(user.user_id)}
                                            >
                                                {/* Top Row: Info */}
                                                <div className="card-top">
                                                    <div className="user-avatar-wrapper">
                                                        {/* Replaced Avatar with Booth Number */}
                                                        <div className="avatar-placeholder" title="Booth Number">
                                                            {user.booth_no || txt.boothPlaceholder}
                                                        </div>
                                                    </div>
                                                    <div className="user-info">
                                                        {/* Name Row */}
                                                        <h3 className="user-name">{user.name}</h3>
                                                        
                                                        {/* Details Row: Mobile + Designation + Call Icon (Removed Booth Label) */}
                                                        <div className="user-details-row">
                                                            <span className="user-mobile">{user.mobile_no}</span>
                                                            <span className="dot-separator">•</span>
                                                            <span className="user-designation">{user.designation || user.user_type}</span>
                                                            
                                                            {/* Added e.stopPropagation() so calling doesn't trigger card click */}
                                                            <a 
                                                                href={`tel:${user.mobile_no}`} 
                                                                className="call-btn-mini" 
                                                                title="Call"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <svg width="12" height="12" fill="white" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Middle Row: Total / Available / Unavailable Boxes */}
                                                <div className="card-middle-grid">
                                                    <div className="status-box box-blue">
                                                        <span className="status-label">{txt.totalSurvey}</span>
                                                        <span className="status-value">{user.total_survey}</span>
                                                    </div>
                                                    <div className="status-box box-success">
                                                        <span className="status-label">{txt.available}</span>
                                                        <span className="status-value">{completed}</span>
                                                    </div>
                                                    <div className="status-box box-danger">
                                                        <span className="status-label">{txt.unavailable}</span>
                                                        <span className="status-value">{user.NA || 0}</span>
                                                    </div>
                                                </div>

                                                {/* Bottom Row: Detailed Stats Grid */}
                                                <div className="card-stats-grid">
                                                    <div className="stat-col">
                                                        <span className="stat-val text-green">{user.P || 0}</span>
                                                        <span className="stat-lbl">{txt.positive}</span>
                                                    </div>
                                                    <div className="stat-col border-left">
                                                        <span className="stat-val text-red">{user.N || 0}</span>
                                                        <span className="stat-lbl">{txt.negative}</span>
                                                    </div>
                                                    <div className="stat-col border-left">
                                                        <span className="stat-val text-orange">{user.D || 0}</span>
                                                        <span className="stat-lbl">{txt.doubtful}</span>
                                                    </div>
                                                    <div className="stat-col border-left">
                                                        <span className="stat-val text-blue">{user.C || 0}</span>
                                                        <span className="stat-lbl">{txt.none}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="col-12 text-center text-muted p-4">
                                    {txt.noRecordsFound} "{searchTerm}"
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                /* Search Bar Styling */
                .search-box-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 300px;
                }
                .header-search-input {
                    width: 100%;
                    padding: 10px 10px 10px 40px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    outline: none;
                    font-size: 14px;
                    background-color: white;
                    color: #1e293b;
                    transition: border-color 0.2s;
                }
                .header-search-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                }

                /* Enhanced Card Styles */
                .user-survey-card {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .user-survey-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
                }

                /* Top Section */
                .card-top {
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    border-bottom: 1px solid #f8fafc;
                    position: relative;
                }
                .user-avatar-wrapper {
                    flex-shrink: 0;
                }
                .avatar-placeholder {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background-color: #eff6ff;
                    color: #3b82f6;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 20px;
                    text-transform: uppercase;
                }
                .user-info {
                    flex: 1;
                    min-width: 0;
                }
                .user-name {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 6px 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                
                /* Details Row Styling */
                .user-details-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    width: 100%;
                    flex-wrap: wrap;
                }
                .user-mobile {
                    font-size: 13px;
                    color: #64748b;
                    margin: 0;
                    font-weight: 500;
                }
                .dot-separator {
                    font-size: 10px;
                    color: #cbd5e1;
                }
                .user-designation {
                    font-size: 12px;
                    font-weight: 600;
                    color: #475569;
                    background-color: #f1f5f9;
                    padding: 2px 8px;
                    border-radius: 4px;
                    white-space: nowrap;
                    max-width: 120px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .call-btn-mini {
                    width: 28px;
                    height: 28px;
                    background-color: #3b82f6;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: 0.2s;
                    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
                    margin-left: auto; /* Pushes button to far right */
                }
                .call-btn-mini:hover {
                    background-color: #2563eb;
                    transform: scale(1.1);
                }

                /* Middle Section (Status Boxes) */
                .card-middle-grid {
                    padding: 16px 20px;
                    display: flex;
                    gap: 10px;
                    background-color: #fff;
                }
                .status-box {
                    flex: 1;
                    border-radius: 10px;
                    padding: 10px 5px; 
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    border-width: 1px;
                    border-style: solid;
                }
                .box-blue {
                    background-color: #eff6ff;
                    border-color: #bfdbfe;
                    color: #1e40af;
                }
                .box-success {
                    background-color: #f0fdf4;
                    border-color: #dcfce7;
                    color: #166534;
                }
                .box-danger {
                    background-color: #fef2f2;
                    border-color: #fee2e2;
                    color: #991b1b;
                }
                .status-label {
                    font-size: 10px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                    opacity: 0.8;
                    white-space: nowrap;
                }
                .status-value {
                    font-size: 16px;
                    font-weight: 800;
                }

                /* Bottom Section (Detailed Stats) */
                .card-stats-grid {
                    display: flex;
                    border-top: 1px solid #f1f5f9;
                    background-color: #fafafa;
                    padding: 12px 0;
                }
                .stat-col {
                    flex: 1;
                    padding: 4px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                }
                .border-left {
                    border-left: 1px solid #e2e8f0;
                }
                .stat-val {
                    font-size: 16px;
                    font-weight: 800;
                    margin-bottom: 2px;
                }
                .stat-lbl {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                }
                
                /* Text Colors */
                .text-green { color: #10b981; }
                .text-red { color: #ef4444; }
                .text-orange { color: #f59e0b; }
                .text-blue { color: #6366f1; }

                /* =========================================
                   RESPONSIVE MEDIA QUERIES (MOBILE FIXES)
                   ========================================= */
                @media (max-width: 768px) {
                    .search-box-wrapper {
                        max-width: 100%; /* Makes search bar full width on mobile */
                        margin-top: 8px;
                    }
                    .page-header {
                        align-items: flex-start !important;
                    }
                    .header-content {
                        margin-bottom: 8px;
                    }
                    .card-top {
                        padding: 16px;
                    }
                    .card-middle-grid {
                        padding: 12px 16px;
                        gap: 8px;
                    }
                    .status-value {
                        font-size: 14px;
                    }
                    .status-label {
                        font-size: 9px;
                    }
                }

                @media (max-width: 480px) {
                    .avatar-placeholder {
                        width: 42px;
                        height: 42px;
                        font-size: 16px;
                    }
                    .user-name {
                        font-size: 15px;
                    }
                    .user-mobile {
                        font-size: 12px;
                    }
                    .user-designation {
                        font-size: 10px;
                        max-width: 90px;
                    }
                    .card-middle-grid {
                        padding: 10px 12px;
                        gap: 6px;
                    }
                    .status-label {
                        font-size: 8px;
                        letter-spacing: -0.2px;
                    }
                    .stat-val {
                        font-size: 14px;
                    }
                    .stat-lbl {
                        font-size: 9px;
                        letter-spacing: -0.5px; /* Prevents columns from overflowing on small screens */
                    }
                }
            `}</style>
        </div>
    );
};

export default BoothPramukhWiseSurvey;